import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import Tooltip from '../../components/shared/Tooltip';
import type { Tenant, User, DealerGroup, TenantSettings } from '../../types';
import FormError from '../../components/shared/FormError';
import { autoCapFirst } from '../../utils/strings';

const FLAG_DEFS: Array<{ key: keyof TenantSettings['featureFlags']; label: string; description: string }> = [
  { key: 'coachingEnabled',        label: 'Coaching',                description: 'Auto-generate coaching notes after scoring.' },
  { key: 'outcomeCorrelation',     label: 'Outcome Correlation',     description: 'Correlate video performance with DMS deal outcomes.' },
  { key: 'crmAutoFetch',           label: 'CRM Auto-Fetch',          description: 'Auto-populate buyer name from CRM before upload.' },
  { key: 'dynamicComplianceRules', label: 'Custom Compliance Rules', description: 'Allow this account to override platform compliance rules.' },
];

interface CreateTenantForm {
  name: string;
  dealershipName: string;
  organizationEmail: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
}

interface EditTenantForm {
  name: string;
  dealershipName: string;
}

interface AddUserForm {
  firstName: string;
  lastName: string;
  email: string;
  role: 'TENANT_ADMIN' | 'SALES_REP';
}

const PAGE_SIZE = 10;
const USER_PAGE_SIZE = 10;

const EMPTY_FORM: CreateTenantForm = {
  name: '',
  dealershipName: '',
  organizationEmail: '',
  ownerFirstName: '',
  ownerLastName: '',
  ownerEmail: '',
};

const EMPTY_USER_FORM: AddUserForm = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'TENANT_ADMIN',
};

const TenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateTenantForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(tenants.length / PAGE_SIZE);
  const paginatedTenants = tenants.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Clamp currentPage when tenants list shrinks (e.g. after delete on last page)
  useEffect(() => {
    const newTotal = Math.ceil(tenants.length / PAGE_SIZE);
    if (newTotal > 0 && currentPage > newTotal) {
      setCurrentPage(newTotal);
    }
  }, [tenants, currentPage]);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditTenantForm>({ name: '', dealershipName: '' });
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteName, setConfirmDeleteName] = useState('');

  // Expanded tenant (show users)
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tenantUsers, setTenantUsers] = useState<Record<string, { users: User[]; total: number }>>({});
  const [loadingUsers, setLoadingUsers] = useState<string | null>(null);
  const [tenantUserPages, setTenantUserPages] = useState<Record<string, number>>({});

  // Add user form
  const [addUserForTenant, setAddUserForTenant] = useState<string | null>(null);
  const [userForm, setUserForm] = useState<AddUserForm>(EMPTY_USER_FORM);
  const [addingUser, setAddingUser] = useState(false);

  // Form validation errors
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [userFormErrors, setUserFormErrors] = useState<Record<string, string>>({});
  const [editNameError, setEditNameError] = useState('');

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // Edit user
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editUserForm, setEditUserForm] = useState<{ firstName: string; lastName: string; email: string; role: 'TENANT_ADMIN' | 'SALES_REP' }>({ firstName: '', lastName: '', email: '', role: 'SALES_REP' });
  const [savingUser, setSavingUser] = useState(false);

  // Delete user confirm
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState<string | null>(null);
  const [confirmDeleteUserTenantId, setConfirmDeleteUserTenantId] = useState<string | null>(null);
  const [confirmDeleteUserName, setConfirmDeleteUserName] = useState('');
  const [deletingUser, setDeletingUser] = useState(false);

  // Dealer groups (for assignment dropdown)
  const [dealerGroups, setDealerGroups] = useState<DealerGroup[]>([]);

  // Expanded tab per tenant: 'personnel' | 'settings'
  const [expandedTab, setExpandedTab] = useState<Record<string, 'personnel' | 'settings'>>({});

  // Feature flag toggle saving state: tenantId:flagKey
  const [togglingFlag, setTogglingFlag] = useState<string | null>(null);

  // Dealer group assignment saving state per tenant
  const [assigningGroup, setAssigningGroup] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    if (type === 'success') toast.success(message);
    else toast.error(message);
  }, []);

  const fetchDealerGroups = useCallback(async () => {
    try {
      const res = await api.get('/admin/dealer-groups');
      setDealerGroups(res.data.data ?? []);
    } catch {
      // non-critical
    }
  }, []);

  const handleToggleFlag = useCallback(async (
    tenant: Tenant,
    flagKey: keyof TenantSettings['featureFlags'],
  ) => {
    const key = `${tenant.id}:${flagKey}`;
    setTogglingFlag(key);
    const current = tenant.settings?.featureFlags?.[flagKey] ?? false;
    const label = FLAG_DEFS.find((f) => f.key === flagKey)?.label ?? flagKey;
    try {
      const res = await api.patch(`/admin/tenants/${tenant.id}/settings`, {
        featureFlags: { [flagKey]: !current },
      });
      const updated = (res.data as { data: Tenant }).data;
      setTenants((prev) =>
        prev.map((t) => (t.id === tenant.id ? { ...t, settings: updated.settings } : t)),
      );
      showToast(`${label} ${!current ? 'enabled' : 'disabled'} for ${tenant.name}.`, 'success');
    } catch {
      showToast(`Failed to update ${label}.`, 'error');
    } finally {
      setTogglingFlag(null);
    }
  }, [showToast]);

  const handleAssignGroup = useCallback(async (tenantId: string, dealerGroupId: string | null) => {
    setAssigningGroup(tenantId);
    try {
      const res = await api.patch(`/admin/tenants/${tenantId}/dealer-group`, { dealerGroupId });
      const updated = (res.data as { data: Tenant }).data;
      setTenants((prev) =>
        prev.map((t) => (t.id === tenantId ? { ...t, dealerGroupId: updated.dealerGroupId } : t)),
      );
      showToast(dealerGroupId ? 'Dealer group assigned.' : 'Removed from dealer group.', 'success');
    } catch {
      showToast('Failed to update dealer group.', 'error');
    } finally {
      setAssigningGroup(null);
    }
  }, [showToast]);

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/tenants');
      const data = res.data.data ?? res.data;
      setTenants(Array.isArray(data) ? data : []);
    } catch {
      showToast('Failed to load tenants.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchTenants();
    fetchDealerGroups();
  }, [fetchTenants, fetchDealerGroups]);

  /* ---------- Fetch users for tenant ---------- */
  const fetchTenantUsers = useCallback(async (tenantId: string, page: number) => {
    setLoadingUsers(tenantId);
    try {
      const res = await api.get(`/admin/tenants/${tenantId}/users`, {
        params: { page, limit: USER_PAGE_SIZE },
      });
      const users: User[] = res.data.data ?? [];
      const total: number = res.data.meta?.total ?? users.length;
      setTenantUsers((prev) => ({ ...prev, [tenantId]: { users, total } }));
      setTenantUserPages((prev) => ({ ...prev, [tenantId]: page }));
    } catch {
      showToast('Failed to load users.', 'error');
    } finally {
      setLoadingUsers(null);
    }
  }, [showToast]);

  const toggleExpand = (tenantId: string) => {
    if (expandedId === tenantId) {
      setExpandedId(null);
      setAddUserForTenant(null);
    } else {
      setExpandedId(tenantId);
      setAddUserForTenant(null);
      fetchTenantUsers(tenantId, tenantUserPages[tenantId] ?? 1);
    }
  };

  /* ---------- Create Tenant ---------- */
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Organization name is required';
    if (!form.ownerFirstName.trim()) errs.ownerFirstName = 'First name is required';
    if (!form.ownerLastName.trim()) errs.ownerLastName = 'Last name is required';
    if (!form.ownerEmail.trim()) errs.ownerEmail = 'Email is required';
    else if (!isValidEmail(form.ownerEmail)) errs.ownerEmail = 'Enter a valid email address';
    if (form.organizationEmail.trim() && !isValidEmail(form.organizationEmail)) errs.organizationEmail = 'Enter a valid email address';
    if (Object.keys(errs).length > 0) { setCreateErrors(errs); return; }
    setCreateErrors({});
    setSubmitting(true);
    try {
      await api.post('/admin/tenants', {
        name: form.name.trim(),
        dealershipName: form.dealershipName.trim() || undefined,
        organizationEmail: form.organizationEmail.trim() || undefined,
        ownerFirstName: form.ownerFirstName.trim(),
        ownerLastName: form.ownerLastName.trim(),
        ownerEmail: form.ownerEmail.trim().toLowerCase(),
      });
      showToast(`Tenant "${form.name}" created successfully.`, 'success');
      setForm(EMPTY_FORM);
      setCreateErrors({});
      setShowCreate(false);
      await fetchTenants();
    } catch {
      showToast('Failed to create tenant.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Edit Tenant ---------- */
  const startEdit = (tenant: Tenant) => {
    setEditingId(tenant.id);
    setEditForm({ name: tenant.name, dealershipName: tenant.dealershipName ?? '' });
    setEditNameError('');
  };

  const handleSaveEdit = async (id: string) => {
    if (!editForm.name.trim()) {
      setEditNameError('Organization name is required');
      return;
    }
    setEditNameError('');
    setSaving(true);
    try {
      await api.patch(`/admin/tenants/${id}`, {
        name: editForm.name.trim(),
        dealershipName: editForm.dealershipName.trim() || null,
      });
      showToast('Tenant updated.', 'success');
      setEditingId(null);
      await fetchTenants();
    } catch {
      showToast('Failed to update tenant.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Suspend / Activate ---------- */
  const handleToggleActive = async (tenant: Tenant) => {
    try {
      if (tenant.isActive) {
        await api.patch(`/admin/tenants/${tenant.id}/suspend`);
        showToast(`"${tenant.name}" suspended.`, 'success');
      } else {
        await api.patch(`/admin/tenants/${tenant.id}/activate`);
        showToast(`"${tenant.name}" reactivated.`, 'success');
      }
      await fetchTenants();
    } catch {
      showToast('Failed to update tenant status.', 'error');
    }
  };

  /* ---------- Delete ---------- */
  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await api.delete(`/admin/tenants/${confirmDeleteId}`);
      showToast('Tenant deleted.', 'success');
      setConfirmDeleteId(null);
      if (expandedId === confirmDeleteId) setExpandedId(null);
      await fetchTenants();
    } catch {
      showToast('Failed to delete tenant. It may have dependent data.', 'error');
    }
  };

  /* ---------- Add User ---------- */
  const handleAddUser = async (tenantId: string) => {
    const errs: Record<string, string> = {};
    if (!userForm.firstName.trim()) errs.firstName = 'First name is required';
    if (!userForm.lastName.trim()) errs.lastName = 'Last name is required';
    if (!userForm.email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(userForm.email)) errs.email = 'Enter a valid email address';
    if (Object.keys(errs).length > 0) { setUserFormErrors(errs); return; }
    setUserFormErrors({});
    setAddingUser(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/users`, {
        firstName: userForm.firstName.trim(),
        lastName: userForm.lastName.trim(),
        email: userForm.email.trim().toLowerCase(),
        role: userForm.role,
      });
      showToast(`User "${userForm.email}" added successfully. Credentials sent via email.`, 'success');
      setUserForm(EMPTY_USER_FORM);
      setUserFormErrors({});
      setAddUserForTenant(null);
      await fetchTenantUsers(tenantId, 1);
    } catch {
      showToast('Failed to add user. Email may already exist.', 'error');
    } finally {
      setAddingUser(false);
    }
  };

  /* ---------- Edit User ---------- */
  const startEditUser = (user: User) => {
    setEditingUserId(user.id);
    setEditUserForm({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      email: user.email,
      role: (user.role === 'TENANT_ADMIN' ? 'TENANT_ADMIN' : 'SALES_REP') as 'TENANT_ADMIN' | 'SALES_REP',
    });
  };

  const handleSaveUser = async (tenantId: string, userId: string) => {
    setSavingUser(true);
    try {
      await api.patch(`/admin/tenants/${tenantId}/users/${userId}`, {
        firstName: editUserForm.firstName.trim(),
        lastName: editUserForm.lastName.trim(),
        email: editUserForm.email.trim(),
        role: editUserForm.role,
      });
      showToast('User updated.', 'success');
      setEditingUserId(null);
      await fetchTenantUsers(tenantId, tenantUserPages[tenantId] ?? 1);
    } catch {
      showToast('Failed to update user.', 'error');
    } finally {
      setSavingUser(false);
    }
  };

  /* ---------- Toggle User Active ---------- */
  const handleToggleUserActive = async (tenantId: string, user: User) => {
    try {
      await api.patch(`/admin/tenants/${tenantId}/users/${user.id}`, {
        isActive: !user.isActive,
      });
      showToast(`User ${user.isActive ? 'deactivated' : 'reactivated'}.`, 'success');
      await fetchTenantUsers(tenantId, tenantUserPages[tenantId] ?? 1);
    } catch {
      showToast('Failed to update user status.', 'error');
    }
  };

  /* ---------- Delete User ---------- */
  const handleDeleteUser = async () => {
    if (!confirmDeleteUserId || !confirmDeleteUserTenantId) return;
    setDeletingUser(true);
    try {
      await api.delete(`/admin/tenants/${confirmDeleteUserTenantId}/users/${confirmDeleteUserId}`);
      showToast('User removed successfully.', 'success');
      setConfirmDeleteUserId(null);
      await fetchTenantUsers(confirmDeleteUserTenantId, 1);
    } catch {
      showToast('Failed to remove user.', 'error');
    } finally {
      setDeletingUser(false);
    }
  };

  const getRoleBadge = (role: string) => {
    if (role === 'TENANT_ADMIN') return { label: 'Admin', cls: 'bg-primary/10 text-primary border-primary/20 ring-primary/10' };
    if (role === 'SALES_REP') return { label: 'Sales Rep', cls: 'bg-secondary/10 text-secondary border-secondary/20 ring-secondary/10' };
    return { label: role, cls: 'bg-on-surface/5 text-on-surface/40 border-outline-variant/20 ring-transparent' };
  };

  return (
    <div className="min-h-screen bg-surface text-on-surface transition-colors duration-300">
      <div className="flex flex-col">
        <TopNav />
        <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">Accounts</h1>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                Manage {tenants.length} dealership environments
              </p>
            </div>

            <button
              onClick={() => setShowCreate(true)}
              className="group/button inline-flex shrink-0 items-center justify-center border border-transparent bg-clip-padding text-sm whitespace-nowrap outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5 [a]:hover:bg-primary/80 gap-2 h-11 px-8 rounded-xl font-bold bg-primary hover:bg-primary/90 text-on-primary transition-all active:scale-95 border-none shadow-lg shadow-primary/10 hover:shadow-primary/20 cursor-pointer"
            >
              <span className="material-symbols-outlined !text-[22px] text-on-primary/70 group-hover:text-on-primary transition-colors">add_circle</span>
              New Account
            </button>
          </div>



          {/* Tenants List */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-32 gap-4">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
              <span className="font-label text-[9px] tracking-widest uppercase text-on-surface/30 font-black">Syncing...</span>
            </div>
          ) : tenants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 bg-surface-container/10 border border-outline-variant/20 rounded-2xl border-dashed">
              <div className="w-16 h-16 bg-on-surface/5 rounded-xl flex items-center justify-center text-on-surface/10 mb-6">
                <span className="material-symbols-outlined text-4xl">corporate_fare</span>
              </div>
              <p className="text-on-surface/20 font-headline text-lg tracking-widest uppercase font-black">No Accounts</p>
              <button
                onClick={() => setShowCreate(true)}
                className="mt-6 text-primary hover:text-primary/70 font-label text-[9px] tracking-wider uppercase font-black transition-all underline underline-offset-4"
              >
                Onboard Dealership
              </button>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 gap-4">
              {paginatedTenants.map((tenant) => {
                const isExpanded = expandedId === tenant.id;
                const tenantUserData = tenantUsers[tenant.id];
                const users = tenantUserData?.users ?? [];
                const userTotal = tenantUserData?.total ?? 0;
                const isLoadingThisUsers = loadingUsers === tenant.id;
                const userCurrentPage = tenantUserPages[tenant.id] ?? 1;
                const userTotalPages = Math.ceil(userTotal / USER_PAGE_SIZE);

                return (
                  <div
                    key={tenant.id}
                    className={`group scroll-mt-6 bg-surface border transition-all duration-500 overflow-hidden ${isExpanded
                      ? 'rounded-2xl border-primary/30 shadow-2xl shadow-primary/20 ring-1 ring-primary/10'
                      : 'rounded-xl border-outline-variant/30 hover:border-primary/20 hover:shadow-xl'
                      }`}
                  >
                    <div className={`flex flex-col lg:flex-row lg:items-center px-6 py-4 gap-4 md:gap-6 ${isExpanded ? 'border-b border-outline-variant/10' : ''}`}>
                      <div className="flex flex-1 items-center gap-4 min-w-0">
                        {/* Expand toggle back to left, but minimal */}
                        <button
                          onClick={() => toggleExpand(tenant.id)}
                          className={`rounded-lg flex items-center justify-center transition-all duration-300 ${isExpanded ? 'bg-primary/10 text-primary' : 'text-on-surface/20 hover:text-primary hover:bg-primary/5'
                            }`}
                        >
                          <span className={`material-symbols-outlined text-[20px] transition-transform duration-500 ${isExpanded ? 'rotate-180' : ''}`}>
                            expand_more
                          </span>
                        </button>

                        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 items-center">
                          {editingId === tenant.id ? (
                            <div className="col-span-2 flex flex-col gap-1">
                              <div className="flex flex-col md:flex-row gap-4">
                                <input
                                  type="text"
                                  value={editForm.name}
                                  onChange={(e) => { setEditForm({ ...editForm, name: autoCapFirst(e.target.value) }); if (editNameError) setEditNameError(''); }}
                                  placeholder="Organization Name *"
                                  className={`flex-1 bg-transparent border-b pb-1 text-on-surface text-sm focus:outline-none font-medium ${
                                    editNameError ? 'border-red-500 placeholder:text-red-400' : 'border-primary'
                                  }`}
                                />
                                <input
                                  type="text"
                                  value={editForm.dealershipName}
                                  onChange={(e) => setEditForm({ ...editForm, dealershipName: autoCapFirst(e.target.value) })}
                                  placeholder="Dealership Name"
                                  className="flex-1 bg-transparent border-b border-primary pb-1 text-on-surface text-sm focus:outline-none font-medium"
                                />
                              </div>
                              <FormError message={editNameError} />
                            </div>
                          ) : (
                            <>
                              <div className="col-span-1 md:col-span-2 flex items-center gap-5 cursor-pointer group/title" onClick={() => toggleExpand(tenant.id)}>
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl font-bold transition-all duration-500 flex-shrink-0 ${isExpanded ? 'bg-primary text-on-primary shadow-lg shadow-primary/20' : 'bg-on-surface/5 text-on-surface/20 group-hover/title:bg-primary/10 group-hover/title:text-primary border border-outline-variant/30'
                                  }`}>
                                  {tenant.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <h3 className={`text-lg font-bold truncate transition-colors ${isExpanded ? 'text-primary' : 'text-on-surface group-hover/title:text-primary'}`}>
                                    {tenant.name}
                                  </h3>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="material-symbols-outlined text-[14px] text-on-surface/20">storefront</span>
                                    <span className="text-xs font-label tracking-wide uppercase text-on-surface/40 font-bold truncate">
                                      {tenant.dealershipName || 'Direct Entity'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          <div className="hidden lg:flex flex-col gap-1">
                            <span className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-600">Status</span>
                            <span className={`inline-flex items-center gap-2 text-xs font-bold tracking-wide uppercase ${tenant.isActive ? 'text-primary' : 'text-error'}`}>
                              <span className={`w-2 h-2 rounded-full ${tenant.isActive ? 'bg-primary' : 'bg-error'} shadow-[0_0_8px_rgba(var(--primary-rgb),0.4)] animate-pulse`} />
                              {tenant.isActive ? 'Active' : 'Suspended'}
                            </span>
                          </div>

                          <div className="hidden lg:flex flex-col gap-1">
                            <span className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-600">Created</span>
                            <span className="text-xs font-medium text-slate-900 dark:text-white/60">
                              {new Date(tenant.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 lg:ml-6 mt-4 lg:mt-0 pt-4 lg:pt-0 border-t lg:border-t-0 border-outline-variant/10">

                        {editingId === tenant.id ? (
                          <div className="flex items-center gap-2">
                            <Tooltip content="Commit Changes">
                              <button onClick={() => handleSaveEdit(tenant.id)} disabled={saving} className="w-9 h-9 rounded-xl bg-secondary text-on-secondary flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-md shadow-secondary/10">
                                <span className="material-symbols-outlined text-[18px]">done_all</span>
                              </button>
                            </Tooltip>
                            <Tooltip content="Discard">
                              <button onClick={() => { setEditingId(null); setEditNameError(''); }} className="w-9 h-9 rounded-xl bg-on-surface/5 text-on-surface/40 flex items-center justify-center hover:bg-on-surface/10 hover:text-on-surface transition-all">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                              </button>
                            </Tooltip>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Tooltip content="Edit Configuration">
                              <button onClick={() => startEdit(tenant)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary/10 text-primary hover:bg-primary/20 hover:scale-105 active:scale-95 transition-all duration-200">
                                <span className="material-symbols-outlined text-[18px]">edit_note</span>
                              </button>
                            </Tooltip>
                            <Tooltip content={tenant.isActive ? 'Suspend' : 'Activate'}>
                              <button onClick={() => handleToggleActive(tenant)} className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 ${tenant.isActive ? 'bg-tertiary/10 text-tertiary hover:bg-tertiary/20' : 'bg-secondary/10 text-secondary hover:bg-secondary/20'}`}>
                                <span className="material-symbols-outlined text-[18px]">{tenant.isActive ? 'block' : 'undo'}</span>
                              </button>
                            </Tooltip>
                            <Tooltip content="Delete Permanent">
                              <button onClick={() => { setConfirmDeleteId(tenant.id); setConfirmDeleteName(tenant.name); }} className="w-9 h-9 flex items-center justify-center rounded-xl bg-error/10 text-error hover:bg-error/20 hover:scale-105 active:scale-95 transition-all duration-200">
                                <span className="material-symbols-outlined text-[18px]">delete</span>
                              </button>
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded: Tabs + Panels */}
                    {isExpanded && (() => {
                      const activeTab = expandedTab[tenant.id] ?? 'personnel';
                      const flags = tenant.settings?.featureFlags ?? {};
                      const assignedGroup = dealerGroups.find((g) => g.id === tenant.dealerGroupId);
                      const isAssigning = assigningGroup === tenant.id;

                      return (
                      <div className="bg-surface border-t border-outline-variant/10 animate-in slide-in-from-top-1 duration-300">
                        {/* Tab bar */}
                        <div className="flex items-center gap-1 px-6 pt-3 pb-0 border-b border-outline-variant/10">
                          {(['personnel', 'settings'] as const).map((tab) => (
                            <button
                              key={tab}
                              onClick={() => setExpandedTab((prev) => ({ ...prev, [tenant.id]: tab }))}
                              className={`px-4 py-2 text-[10px] font-bold tracking-widest uppercase border-b-2 transition-all -mb-px ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-on-surface/30 hover:text-on-surface/60'}`}
                            >
                              {tab === 'personnel' ? `Personnel (${userTotal})` : 'Settings'}
                            </button>
                          ))}
                        </div>

                        {/* Settings Tab */}
                        {activeTab === 'settings' && (
                          <div className="px-6 py-6 md:px-8 space-y-8">

                            {/* Dealer Group Assignment */}
                            <div>
                              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-outline-variant/10">
                                <span className="material-symbols-outlined text-[18px] text-on-surface/30">group_work</span>
                                <h4 className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-600">Dealer Group</h4>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="relative flex-1 max-w-xs">
                                  <select
                                    value={tenant.dealerGroupId ?? ''}
                                    disabled={isAssigning}
                                    onChange={(e) => handleAssignGroup(tenant.id, e.target.value || null)}
                                    className="w-full h-10 bg-surface border-b border-outline-variant/40 focus:border-primary pr-8 pl-3 text-sm font-medium text-on-surface focus:outline-none appearance-none transition-colors disabled:opacity-50 cursor-pointer"
                                  >
                                    <option value="">— Unassigned —</option>
                                    {dealerGroups.filter((g) => g.isActive).map((g) => (
                                      <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                  </select>
                                  {/* <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-[16px] text-on-surface/30 pointer-events-none">expand_more</span> */}
                                </div>
                                {assignedGroup && (
                                  <span className="text-[9px] font-black tracking-widest uppercase text-primary bg-primary/10 px-3 py-1 rounded-full">
                                    {assignedGroup.name}
                                  </span>
                                )}
                                {isAssigning && <div className="w-4 h-4 border-2 border-primary border-t-transparent animate-spin rounded-full" />}
                              </div>
                            </div>

                            {/* Feature Flags */}
                            <div>
                              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-outline-variant/10">
                                <span className="material-symbols-outlined text-[18px] text-on-surface/30">toggle_on</span>
                                <h4 className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-600">Feature Flags</h4>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {FLAG_DEFS.map((flag) => {
                                  const isOn = !!(flags as Record<string, boolean>)[flag.key];
                                  const toggling = togglingFlag === `${tenant.id}:${flag.key}`;
                                  return (
                                    <div key={flag.key} className="flex items-center justify-between gap-4 px-4 py-3 bg-surface-container/30 rounded-xl border border-outline-variant/10 hover:border-primary/20 transition-all">
                                      <div className="min-w-0">
                                        <p className="text-sm font-semibold text-on-surface truncate">{flag.label}</p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">{flag.description}</p>
                                      </div>
                                      <button
                                        onClick={() => handleToggleFlag(tenant, flag.key)}
                                        disabled={toggling}
                                        className={`relative w-10 h-5.5 min-w-[40px] rounded-full transition-all duration-300 ${toggling ? 'opacity-50' : 'cursor-pointer'} ${isOn ? 'bg-primary' : 'bg-on-surface/15'}`}
                                        style={{ minWidth: '40px', height: '22px' }}
                                      >
                                        {toggling ? (
                                          <span className="absolute inset-0 flex items-center justify-center">
                                            <span className="w-3 h-3 border-2 border-white/40 border-t-white animate-spin rounded-full" />
                                          </span>
                                        ) : (
                                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-300 ${isOn ? 'translate-x-[18px]' : 'translate-x-0'}`} />
                                        )}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                          </div>
                        )}

                        {/* Personnel Tab */}
                        {activeTab === 'personnel' && (
                        <div className="px-6 py-6 md:px-8">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6 pb-4 border-b border-outline-variant/10">
                            <div className="flex items-center gap-3">
                              <h4 className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-600">
                                Allocated Personnel ({userTotal})
                              </h4>
                            </div>
                            <button
                              onClick={() => setAddUserForTenant(addUserForTenant === tenant.id ? null : tenant.id)}
                              className="inline-flex items-center gap-2 text-primary hover:text-primary/70 font-label text-[9px] tracking-wider uppercase font-black transition-all"
                            >
                              <span className="material-symbols-outlined text-[16px]">person_add</span>
                              Add Member
                            </button>
                          </div>


                          {/* Users List */}
                          {isLoadingThisUsers ? (
                            <div className="flex flex-col items-center justify-center py-20 gap-4">
                              <div className="w-10 h-10 border-4 border-primary/20 border-t-primary animate-spin rounded-full" />
                              <span className="font-label text-[9px] tracking-[0.2em] uppercase text-on-surface/20 font-black">Scanning resources...</span>
                            </div>
                          ) : users.length === 0 ? (
                            <div className="py-12 border border-outline-variant/10 border-dashed rounded-xl flex flex-col items-center">
                              <span className="material-symbols-outlined text-3xl text-on-surface/10 mb-2">group_off</span>
                              <p className="text-on-surface/20 font-label text-[9px] tracking-widest uppercase font-black">No Personnel</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 gap-px bg-outline-variant/5 rounded-xl border border-outline-variant/10 shadow-sm shadow-black/[0.02]">
                              {users.map((user) => {
                                const badge = getRoleBadge(user.role);
                                const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Unidentified User';
                                const isEditingThis = editingUserId === user.id;

                                return isEditingThis ? (
                                  <div key={user.id} className="p-6 transition-all">
                                    <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-end">
                                      <div className="space-y-1">
                                        <label className="block text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">First</label>
                                        <input type="text" value={editUserForm.firstName} onChange={(e) => setEditUserForm({ ...editUserForm, firstName: autoCapFirst(e.target.value) })} className="w-full bg-transparent border-b border-primary/40 pb-1 text-slate-900 dark:text-white text-sm focus:outline-none focus:border-primary font-medium" />
                                      </div>
                                      <div className="space-y-1">
                                        <label className="block text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Last</label>
                                        <input type="text" value={editUserForm.lastName} onChange={(e) => setEditUserForm({ ...editUserForm, lastName: autoCapFirst(e.target.value) })} className="w-full bg-transparent border-b border-primary/40 pb-1 text-slate-900 dark:text-white text-sm focus:outline-none focus:border-primary font-medium" />
                                      </div>
                                      <div className="space-y-1">
                                        <label className="block text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Email</label>
                                        <input type="email" value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} className="w-full bg-transparent border-b border-primary/40 pb-1 text-slate-900 dark:text-white text-sm focus:outline-none focus:border-primary font-medium" />
                                      </div>
                                      <div className="space-y-1">
                                        <label className="block text-[8px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Role</label>
                                        <select value={editUserForm.role} onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value as any })} className="w-full bg-transparent border-b border-primary/40 pb-1 text-slate-900 dark:text-white text-sm focus:outline-none focus:border-primary font-medium appearance-none">
                                          <option value="TENANT_ADMIN" className="bg-white dark:bg-slate-900">Admin</option>
                                          <option value="SALES_REP" className="bg-white dark:bg-slate-900">Sales Rep</option>
                                        </select>
                                      </div>
                                      <div className="flex items-center gap-2 justify-end">
                                        <button onClick={() => handleSaveUser(tenant.id, user.id)} disabled={savingUser} className="w-10 h-10 rounded-xl bg-secondary text-on-secondary flex items-center justify-center hover:brightness-110 transition-all shadow-md shadow-secondary/10">
                                          <span className="material-symbols-outlined text-[18px]">check</span>
                                        </button>
                                        <button onClick={() => setEditingUserId(null)} className="w-10 h-10 rounded-xl bg-on-surface/5 text-on-surface/40 flex items-center justify-center hover:text-on-surface transition-all">
                                          <span className="material-symbols-outlined text-[18px]">close</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div key={user.id} className="group/user flex flex-col md:flex-row md:items-center gap-6 px-6 py-4 bg-surface transition-all hover:bg-primary/[0.01] border-b last:border-b-0 border-outline-variant/10">
                                    <div className="flex flex-1 items-center gap-6 min-w-0">
                                      {/* App-Icon style avatar */}
                                      <div className="w-11 h-11 rounded-2xl bg-surface-container-highest border border-outline-variant/30 flex items-center justify-center flex-shrink-0 group-hover/user:bg-primary/5 group-hover/user:border-primary/20 transition-all font-bold text-on-surface/30 group-hover/user:text-primary">
                                        {(user.firstName?.charAt(0) ?? '').toUpperCase()}{(user.lastName?.charAt(0) ?? '').toUpperCase()}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-[15px] font-bold text-on-surface tracking-tight truncate">{fullName}</div>
                                        <div className="text-xs font-medium text-on-surface/40 tracking-tight truncate mt-0.5">
                                          {user.email}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-8">
                                      <div className="flex flex-col items-end min-w-[100px]">
                                        <span className="font-label text-[9px] tracking-widest uppercase text-on-surface/20 font-black mb-1">Role</span>
                                        <span className={`px-3 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase border ring-1 ring-inset ${badge.cls}`}>
                                          {badge.label}
                                        </span>
                                      </div>

                                      <div className="hidden xl:flex flex-col items-end min-w-[100px]">
                                        <span className="font-label text-[9px] tracking-widest uppercase text-on-surface/20 font-black mb-1">Last Active</span>
                                        <span className="text-[10px] font-bold text-on-surface/60">
                                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                                        </span>
                                      </div>

                                      <div className="hidden lg:flex flex-col items-end min-w-[90px]">
                                        <span className="font-label text-[9px] tracking-widest uppercase text-on-surface/20 font-black mb-1">Status</span>
                                        <span className={`inline-flex items-center gap-2 text-[10px] font-bold tracking-wider uppercase ${user.isActive ? 'text-primary' : 'text-error'}`}>
                                          <span className={`w-1.5 h-1.5 rounded-full ${user.isActive ? 'bg-primary' : 'bg-error'} shadow-[0_0_6px_rgba(var(--primary-rgb),0.3)]`} />
                                          {user.isActive ? 'Active' : 'Suspended'}
                                        </span>
                                      </div>

                                      <div className="flex items-center gap-2 ml-6">
                                        <Tooltip content="Edit Member">
                                          <button onClick={() => startEditUser(user)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary/10 text-primary hover:bg-primary/20 hover:scale-105 active:scale-95 transition-all duration-200">
                                            <span className="material-symbols-outlined text-[18px]">edit_note</span>
                                          </button>
                                        </Tooltip>
                                        <Tooltip content={user.isActive ? 'Suspend' : 'Activate'}>
                                          <button
                                            onClick={() => handleToggleUserActive(tenant.id, user)}
                                            className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 ${user.isActive ? 'bg-tertiary/10 text-tertiary hover:bg-tertiary/20' : 'bg-secondary/10 text-secondary hover:bg-secondary/20'}`}
                                          >
                                            <span className="material-symbols-outlined text-[18px]">{user.isActive ? 'block' : 'undo'}</span>
                                          </button>
                                        </Tooltip>
                                        <Tooltip content="Deactivate Permanent">
                                          <button
                                            onClick={() => { setConfirmDeleteUserId(user.id); setConfirmDeleteUserTenantId(tenant.id); setConfirmDeleteUserName(fullName); }}
                                            className="w-9 h-9 flex items-center justify-center rounded-xl bg-error/10 text-error hover:bg-error/20 hover:scale-105 active:scale-95 transition-all duration-200"
                                          >
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                          </button>
                                        </Tooltip>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* User Pagination */}
                          {userTotalPages > 1 && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-outline-variant/10">
                              <p className="text-[10px] font-black tracking-widest uppercase text-on-surface/30">
                                Page {userCurrentPage} of {userTotalPages} &middot; {userTotal} members
                              </p>
                              <div className="flex items-center gap-1">
                                <Tooltip content="First page">
                                  <button
                                    onClick={() => fetchTenantUsers(tenant.id, 1)}
                                    disabled={userCurrentPage === 1 || isLoadingThisUsers}
                                    className="w-8 h-8 flex items-center justify-center rounded-xl text-on-surface/30 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                                  >
                                    <span className="material-symbols-outlined text-[16px]">first_page</span>
                                  </button>
                                </Tooltip>
                                <Tooltip content="Previous page">
                                  <button
                                    onClick={() => fetchTenantUsers(tenant.id, userCurrentPage - 1)}
                                    disabled={userCurrentPage === 1 || isLoadingThisUsers}
                                    className="w-8 h-8 flex items-center justify-center rounded-xl text-on-surface/30 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                                  >
                                    <span className="material-symbols-outlined text-[16px]">chevron_left</span>
                                  </button>
                                </Tooltip>
                                {Array.from({ length: userTotalPages }, (_, i) => i + 1)
                                  .filter((p) => p === 1 || p === userTotalPages || Math.abs(p - userCurrentPage) <= 1)
                                  .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                                    acc.push(p);
                                    return acc;
                                  }, [])
                                  .map((p, idx) =>
                                    p === 'ellipsis' ? (
                                      <span key={`ue-${idx}`} className="w-8 h-8 flex items-center justify-center text-on-surface/20 text-sm">…</span>
                                    ) : (
                                      <button
                                        key={p}
                                        onClick={() => fetchTenantUsers(tenant.id, p as number)}
                                        disabled={isLoadingThisUsers}
                                        className={`w-8 h-8 flex items-center justify-center rounded-xl text-[11px] font-black tracking-wider transition-all disabled:cursor-not-allowed ${userCurrentPage === p ? 'bg-primary text-on-primary shadow-md shadow-primary/20' : 'text-on-surface/40 hover:text-primary hover:bg-primary/5'}`}
                                      >
                                        {p}
                                      </button>
                                    )
                                  )}
                                <Tooltip content="Next page">
                                  <button
                                    onClick={() => fetchTenantUsers(tenant.id, userCurrentPage + 1)}
                                    disabled={userCurrentPage === userTotalPages || isLoadingThisUsers}
                                    className="w-8 h-8 flex items-center justify-center rounded-xl text-on-surface/30 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                                  >
                                    <span className="material-symbols-outlined text-[16px]">chevron_right</span>
                                  </button>
                                </Tooltip>
                                <Tooltip content="Last page">
                                  <button
                                    onClick={() => fetchTenantUsers(tenant.id, userTotalPages)}
                                    disabled={userCurrentPage === userTotalPages || isLoadingThisUsers}
                                    className="w-8 h-8 flex items-center justify-center rounded-xl text-on-surface/30 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                                  >
                                    <span className="material-symbols-outlined text-[16px]">last_page</span>
                                  </button>
                                </Tooltip>
                              </div>
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                    );})()}
                  </div>
                );
              })}
            </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-outline-variant/10">
                  <p className="text-[10px] font-black tracking-widest uppercase text-on-surface/30">
                    Page {currentPage} of {totalPages} &middot; {tenants.length} accounts
                  </p>
                  <div className="flex items-center gap-1">
                    <Tooltip content="First page">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="w-9 h-9 flex items-center justify-center rounded-xl text-on-surface/30 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                      >
                        <span className="material-symbols-outlined text-[18px]">first_page</span>
                      </button>
                    </Tooltip>
                    <Tooltip content="Previous page">
                      <button
                        onClick={() => setCurrentPage((p) => p - 1)}
                        disabled={currentPage === 1}
                        className="w-9 h-9 flex items-center justify-center rounded-xl text-on-surface/30 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                      >
                        <span className="material-symbols-outlined text-[18px]">chevron_left</span>
                      </button>
                    </Tooltip>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                      .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                        if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) =>
                        p === 'ellipsis' ? (
                          <span key={`e-${idx}`} className="w-9 h-9 flex items-center justify-center text-on-surface/20 text-sm">…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setCurrentPage(p as number)}
                            className={`w-9 h-9 flex items-center justify-center rounded-xl text-sm font-bold transition-all ${
                              currentPage === p
                                ? 'bg-primary text-on-primary shadow-lg shadow-primary/20'
                                : 'text-on-surface/40 hover:text-primary hover:bg-primary/5'
                            }`}
                          >
                            {p}
                          </button>
                        ),
                      )}
                    <Tooltip content="Next page">
                      <button
                        onClick={() => setCurrentPage((p) => p + 1)}
                        disabled={currentPage === totalPages}
                        className="w-9 h-9 flex items-center justify-center rounded-xl text-on-surface/30 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                      >
                        <span className="material-symbols-outlined text-[18px]">chevron_right</span>
                      </button>
                    </Tooltip>
                    <Tooltip content="Last page">
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="w-9 h-9 flex items-center justify-center rounded-xl text-on-surface/30 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
                      >
                        <span className="material-symbols-outlined text-[18px]">last_page</span>
                      </button>
                    </Tooltip>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Overlays at root level to ensure they cover TopNav */}
      {/* Create Tenant Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
            <div className="px-6 py-5 sm:px-8 sm:pt-8 sm:pb-6 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-white/5 flex items-center justify-between gap-4 sm:gap-5 text-left shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shadow-inner">
                  <span className="material-symbols-outlined text-[24px]">domain_add</span>
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Configuration</h2>
                  <p className="text-[10px] font-black uppercase text-slate-400 dark:text-slate-600 tracking-widest mt-0.5">Terminal Setup</p>
                </div>
              </div>
              <button onClick={() => setShowCreate(false)} className="w-10 h-10 rounded-xl hover:bg-on-surface/5 flex items-center justify-center text-on-surface/20 hover:text-on-surface transition-all border border-transparent hover:border-outline-variant/30">
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            </div>

            <form onSubmit={handleCreate} noValidate className="flex flex-col overflow-hidden">
              <div className="px-6 py-8 sm:px-10 sm:py-10 space-y-10 overflow-y-auto">
                {/* Entity Details */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-100 dark:bg-white/5" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400 dark:text-slate-600">Entity Details</span>
                    <div className="h-px flex-1 bg-slate-100 dark:bg-white/5" />
                  </div>
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Organization Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => { setForm({ ...form, name: autoCapFirst(e.target.value) }); if (createErrors.name) setCreateErrors((p) => ({ ...p, name: '' })); }}
                        placeholder="e.g. Global Motors Group"
                        className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:ring-4 outline-none ${
                          createErrors.name ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5' : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:border-primary focus:ring-primary/5'
                        }`}
                      />
                      <FormError message={createErrors.name} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Trading / Dealership Name</label>
                      <input
                        type="text"
                        value={form.dealershipName}
                        onChange={(e) => setForm({ ...form, dealershipName: autoCapFirst(e.target.value) })}
                        placeholder="e.g. Northside Branch"
                        className="h-12 w-full rounded-xl border border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Corporate Email</label>
                      <input
                        type="email"
                        value={form.organizationEmail}
                        onChange={(e) => { setForm({ ...form, organizationEmail: e.target.value }); if (createErrors.organizationEmail) setCreateErrors((p) => ({ ...p, organizationEmail: '' })); }}
                        placeholder="admin@dealership.com"
                        className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:ring-4 outline-none ${
                          createErrors.organizationEmail ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5' : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:border-primary focus:ring-primary/5'
                        }`}
                      />
                      <FormError message={createErrors.organizationEmail} />
                    </div>
                  </div>
                </div>

                {/* Administrative Access */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-100 dark:bg-white/5" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400 dark:text-slate-600">Administrative Access</span>
                    <div className="h-px flex-1 bg-slate-100 dark:bg-white/5" />
                  </div>
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">First Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={form.ownerFirstName}
                          onChange={(e) => { setForm({ ...form, ownerFirstName: autoCapFirst(e.target.value) }); if (createErrors.ownerFirstName) setCreateErrors((p) => ({ ...p, ownerFirstName: '' })); }}
                          placeholder="John"
                          className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:ring-4 outline-none ${
                            createErrors.ownerFirstName ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5' : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:border-primary focus:ring-primary/5'
                          }`}
                        />
                        <FormError message={createErrors.ownerFirstName} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Last Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          value={form.ownerLastName}
                          onChange={(e) => { setForm({ ...form, ownerLastName: autoCapFirst(e.target.value) }); if (createErrors.ownerLastName) setCreateErrors((p) => ({ ...p, ownerLastName: '' })); }}
                          placeholder="Smith"
                          className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:ring-4 outline-none ${
                            createErrors.ownerLastName ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5' : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:border-primary focus:ring-primary/5'
                          }`}
                        />
                        <FormError message={createErrors.ownerLastName} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Owner / Admin Email <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        value={form.ownerEmail}
                        onChange={(e) => { setForm({ ...form, ownerEmail: e.target.value }); if (createErrors.ownerEmail) setCreateErrors((p) => ({ ...p, ownerEmail: '' })); }}
                        placeholder="j.smith@corporate.com"
                        className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:ring-4 outline-none ${
                          createErrors.ownerEmail ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5' : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:border-primary focus:ring-primary/5'
                        }`}
                      />
                      <FormError message={createErrors.ownerEmail} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-6 sm:px-10 sm:py-8 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-white/5 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="h-12 px-8 rounded-xl font-bold text-red-500 dark:text-red-400 border border-red-50 dark:border-red-900/20 bg-red-50/50 dark:bg-red-950/20 hover:bg-red-50 dark:hover:bg-red-950/40 cursor-pointer transition-all text-xs uppercase tracking-widest"
                >
                  Discard changes
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="h-12 px-10 rounded-xl font-bold bg-primary hover:bg-primary/90 text-on-primary shadow-md shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 transition-all cursor-pointer whitespace-nowrap text-xs uppercase tracking-widest disabled:opacity-50"
                >
                  {submitting ? 'Initializing...' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-10 space-y-8 text-center max-w-md w-full rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="mx-auto p-4 bg-red-50 dark:bg-red-950/30 rounded-full w-fit">
              <span className="material-symbols-outlined text-[32px] text-red-500 dark:text-red-400 block">delete</span>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Delete Account</h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-sm leading-relaxed">
                Are you sure you want to delete <strong>{confirmDeleteName}</strong>?<br />
                This action is permanent and cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-4">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="flex-1 h-12 rounded-xl font-bold border border-red-50 dark:border-red-900/20 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all cursor-pointer text-xs uppercase tracking-widest"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 h-12 rounded-xl font-bold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white shadow-lg shadow-red-500/10 dark:shadow-red-900/40 transition-all cursor-pointer text-xs uppercase tracking-widest"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {addUserForTenant && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
            <div className="px-6 py-5 sm:px-8 sm:pt-8 sm:pb-6 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-white/5 flex items-center justify-between gap-4 sm:gap-5 text-left shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shadow-inner">
                  <span className="material-symbols-outlined text-[24px]">person_add</span>
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Authorize Member</h2>
                  <p className="text-[10px] font-black uppercase text-slate-400 dark:text-slate-600 tracking-widest mt-0.5">Personnel Allocation</p>
                </div>
              </div>
              <button onClick={() => { setAddUserForTenant(null); setUserForm(EMPTY_USER_FORM); }} className="w-10 h-10 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all border border-transparent hover:border-outline-variant/30">
                <span className="material-symbols-outlined text-lg">close</span>
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleAddUser(addUserForTenant); }} noValidate className="flex flex-col overflow-hidden">
              <div className="px-6 py-8 sm:px-10 sm:py-10 space-y-8 overflow-y-auto thin-scrollbar">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">First Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={userForm.firstName}
                      onChange={(e) => { setUserForm({ ...userForm, firstName: autoCapFirst(e.target.value) }); if (userFormErrors.firstName) setUserFormErrors((p) => ({ ...p, firstName: '' })); }}
                      placeholder="John"
                      className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:ring-4 outline-none ${
                        userFormErrors.firstName ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5' : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:border-primary focus:ring-primary/5'
                      }`}
                    />
                    <FormError message={userFormErrors.firstName} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Last Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={userForm.lastName}
                      onChange={(e) => { setUserForm({ ...userForm, lastName: autoCapFirst(e.target.value) }); if (userFormErrors.lastName) setUserFormErrors((p) => ({ ...p, lastName: '' })); }}
                      placeholder="Smith"
                      className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:ring-4 outline-none ${
                        userFormErrors.lastName ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5' : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:border-primary focus:ring-primary/5'
                      }`}
                    />
                    <FormError message={userFormErrors.lastName} />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => { setUserForm({ ...userForm, email: e.target.value }); if (userFormErrors.email) setUserFormErrors((p) => ({ ...p, email: '' })); }}
                    placeholder="john@example.com"
                    className={`h-12 w-full rounded-xl border px-6 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-700 transition-all focus:bg-white dark:focus:bg-slate-800 focus:ring-4 outline-none ${
                      userFormErrors.email ? 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20 focus:border-red-400 focus:ring-red-500/5' : 'border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 focus:border-primary focus:ring-primary/5'
                    }`}
                  />
                  <FormError message={userFormErrors.email} />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">Access Level <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({ ...userForm, role: e.target.value as any })}
                      className="h-12 w-full rounded-xl border border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 px-6 text-sm font-medium text-slate-900 dark:text-white transition-all focus:bg-white dark:focus:bg-slate-800 focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none appearance-none cursor-pointer"
                    >
                      <option value="TENANT_ADMIN">Account Administrator</option>
                      <option value="SALES_REP">Sales Representative</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-lg">expand_more</span>
                  </div>
                </div>
              </div>

              <div className="px-6 py-6 sm:px-10 sm:py-8 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-white/5 shrink-0">
                <button
                  type="button"
                  onClick={() => { setAddUserForTenant(null); setUserForm(EMPTY_USER_FORM); }}
                  className="px-8 h-12 rounded-xl font-bold border border-red-50 dark:border-red-900/20 text-red-500 dark:text-red-400 bg-red-50/50 dark:bg-red-950/20 hover:bg-red-50 dark:hover:bg-red-950/40 cursor-pointer transition-all text-xs uppercase tracking-widest"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  disabled={addingUser}
                  className="h-12 px-10 rounded-xl font-bold bg-primary hover:bg-primary/90 text-on-primary shadow-md shadow-primary/10 hover:shadow-xl hover:shadow-primary/20 transition-all cursor-pointer whitespace-nowrap text-xs uppercase tracking-widest disabled:opacity-50"
                >
                  {addingUser ? 'Initializing...' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {confirmDeleteUserId && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-10 space-y-8 text-center max-w-md w-full rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="mx-auto p-4 bg-red-50 dark:bg-red-950/30 rounded-full w-fit">
              <span className="material-symbols-outlined text-[32px] text-red-500 dark:text-red-400 block">person_remove</span>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight uppercase tracking-widest">Remove User?</h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-sm leading-relaxed px-4">
                This will permanently remove <strong>{confirmDeleteUserName}</strong> from the platform.<br />
                This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-4">
              <button
                onClick={() => setConfirmDeleteUserId(null)}
                disabled={deletingUser}
                className="flex-1 h-12 rounded-xl font-bold border border-red-50 dark:border-red-900/20 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deletingUser}
                className="flex-1 h-12 rounded-xl font-bold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white shadow-lg shadow-red-500/10 dark:shadow-red-900/40 transition-all text-xs uppercase tracking-widest disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deletingUser ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                    Removing...
                  </>
                ) : (
                  'Remove'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;
